import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useMemo } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@volca/ui/components/ui/sheet";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useIsMobile } from "@volca/ui/hooks/use-mobile";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import { formatMoney } from "@/core/utils/money";
import type { IcmLineItem } from "@/icm/types";
import { formatTraceValue, humanizeKey } from "@/icm/utils";

export const TechnicianLineItemSheet = ({
  lineItem,
  open,
  onOpenChange,
}: {
  lineItem: IcmLineItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const isMobile = useIsMobile();

  const traceEntries = useMemo(
    () =>
      lineItem
        ? Object.entries(lineItem.calculation_trace).map(([key, value]) => ({
            key,
            label: humanizeKey(key),
            value,
          }))
        : [],
    [lineItem],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        showCloseButton={false}
        className={cn("overflow-hidden", {
          "rounded-t-2xl": isMobile,
          "h-full w-full sm:max-w-[500px]": !isMobile,
        })}
      >
        {lineItem && (
          <Stack align="start" gap={0} className="h-full w-full">
            <SheetTitle className="sr-only">
              {lineItem.payout_rule_name}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Line item payout details
            </SheetDescription>
            <Stack align="start" gap={6} className="w-full p-6">
              <Stack
                className="w-full"
                gap={4}
                direction="row"
                justify="between"
                align="start"
              >
                <Stack align="start" gap={1}>
                  <Text variant="heading">{lineItem.payout_rule_name}</Text>
                  {lineItem.customer_name && (
                    <Text tone="muted">{lineItem.customer_name}</Text>
                  )}
                </Stack>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label="Close"
                  onClick={() => onOpenChange(false)}
                >
                  <XIcon className="size-6" />
                </Button>
              </Stack>

              <Stack className="w-full" align="start" gap={4}>
                <Stack className="w-full" align="start" direction="row" gap={8}>
                  {lineItem.job_completed_on && (
                    <Stack align="start" gap={1} className="w-[50%]">
                      <Text variant="label" tone="muted">
                        Completed
                      </Text>
                      <Text>
                        {dayjs(lineItem.job_completed_on).format("MMM D, YYYY")}
                      </Text>
                    </Stack>
                  )}
                  <Stack align="start" gap={1} className="w-[50%]">
                    <Text variant="label" tone="muted">
                      Payout
                    </Text>
                    <Text>{formatMoney(lineItem.payout_amount) ?? "–"}</Text>
                  </Stack>
                </Stack>
                {lineItem.business_unit_name && (
                  <Stack align="start" gap={1}>
                    <Text variant="label" tone="muted">
                      Business unit
                    </Text>
                    <Text>{lineItem.business_unit_name}</Text>
                  </Stack>
                )}
                {lineItem.job_type_name && (
                  <Stack align="start" gap={1}>
                    <Text variant="label" tone="muted">
                      Job Type
                    </Text>
                    <Text>{lineItem.job_type_name}</Text>
                  </Stack>
                )}
              </Stack>
            </Stack>

            <div className="w-full flex-1 overflow-hidden">
              <div className="h-full w-full overflow-y-auto">
                <Stack align="start" gap={1} className="w-full px-6 pt-2 pb-16">
                  {lineItem.warnings.length > 0 && (
                    <Stack align="start" gap={2} className="w-full pt-4 pb-2">
                      {lineItem.warnings.map((warning) => (
                        <Alert key={warning} color="warning" role="status">
                          <WarningIcon />
                          <AlertDescription>
                            <Text>{warning}</Text>
                          </AlertDescription>
                        </Alert>
                      ))}
                    </Stack>
                  )}
                  {traceEntries.map((entry) => (
                    <Stack
                      className="w-full py-2"
                      align="center"
                      key={entry.key}
                      gap={4}
                      direction="row"
                      justify="between"
                    >
                      <Text tone="muted">{entry.label}</Text>
                      <Text>{formatTraceValue(entry.key, entry.value)}</Text>
                    </Stack>
                  ))}
                </Stack>
              </div>
            </div>
          </Stack>
        )}
      </SheetContent>
    </Sheet>
  );
};
